/**
 * Unavailable / broken page popup.
 *
 * Markup and copy live in snippets/unavailable-popup.liquid.
 *
 * Opens when a menu link marked `data-unavailable-link` is clicked, and
 * prevents the navigation. snippets/menu-link-unavailable.liquid decides which
 * links get marked: those with no destination ("#"), and those pointing at a
 * page that no longer exists (for example /pages/culture). Marking them
 * server-side means the customer never reaches the 404 at all.
 *
 * Deliberately does NOT fire on the 404 page itself. A genuinely invalid URL
 * should get the normal "Page not found" page; "we're curating this page" is
 * the wrong message for a mistyped address.
 *
 * Reuses MinimogTheme.Modal so the open/close motion matches every other modal
 * on the site (scale 0.9 -> 1 with a fade, on --m-duration-default).
 */
if (!customElements.get("m-unavailable-popup")) {
  class MUnavailablePopup extends HTMLElement {
    connectedCallback() {
      this.template = document.getElementById("UnavailablePopupTemplate");
      if (!this.template) return;

      // MinimogTheme.Modal comes from vendor.js, which is deferred like this
      // file. Guard in case load order ever changes.
      if (!window.MinimogTheme || !MinimogTheme.Modal) return;

      this.modal = new MinimogTheme.Modal();
      this.bindDeadLinks();
    }

    buildContent() {
      const wrapper = document.createElement("DIV");
      wrapper.appendChild(this.template.content.cloneNode(true));

      // "Continue Shopping" simply dismisses the popup: the customer never
      // left the page they were on, so closing returns them to it.
      wrapper
        .querySelector("[data-unavailable-continue]")
        ?.addEventListener("click", () => this.close());

      return wrapper;
    }

    open() {
      const content = this.buildContent();
      this.modal.appendChild(content);
      this.modal.setWidth("440px");
      // No m-color-* scheme class here on purpose. The store's drawer/popup
      // scheme is "dark", which rendered this popup dark; the approved design
      // is a light card, so the styling is fixed in the snippet instead.
      this.modal.setSizes("m-unavailable-popup--modal");
      this.modal.open();
    }

    close() {
      // Modal exposes close(); fall back to clicking its own close button.
      if (typeof this.modal.close === "function") {
        this.modal.close();
        return;
      }
      document.querySelector(".m-modal--close")?.click();
    }

    bindDeadLinks() {
      // Only links explicitly marked in Liquid are intercepted.
      //
      // Matching on href="#" alone was wrong: the top-level items (Shop, Our
      // World) and the mega-menu column headings (Shop by Edit, Shop by Weave)
      // also use "#" but open a submenu on hover or tap. The Liquid templates
      // know which entries actually have children, so they decide, not this
      // file. See snippets/header-main-menu-container.liquid and
      // snippets/mega-menu-link.liquid.
      //
      // Delegated so it survives the header being re-rendered by the Theme
      // Editor or the mega menu re-initialising.
      addEventDelegate({
        context: document,
        event: "click",
        selector: "a[data-unavailable-link]",
        handler: (event, target) => {
          // A marked link should never also be a submenu toggle, but if the
          // menu is ever restructured, let the toggle win.
          if (target.hasAttribute("data-toggle-submenu")) return;

          event.preventDefault();
          event.stopPropagation();
          this.open();
        },
      });
    }

  }

  customElements.define("m-unavailable-popup", MUnavailablePopup);
}
